import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
  Alert,
  useTheme,
  alpha,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as ProcessingIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  Replay as ReplayIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  Visibility as ViewResultsIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';
import { useUpstreamModuleCheck } from '../../hooks/useUpstreamModuleCheck';
import config from '../../utils/config';
import BatchResultsDialog from '../../components/workflow-v2/BatchResultsDialog';
import PromptEditDialog from '../../components/workflow-v2/PromptEditDialog';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CopyableText from '../../components/common/CopyableText';

interface BatchStatus {
  module_id: number;
  job_id?: number;
  external_job_id?: string;
  status: string;
  is_active_running?: boolean;  // True if batch thread is actively running
  total_keywords?: number;
  processed_keywords?: number;
  error_records?: number;
  progress_percentage?: number;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

const BatchRunStep: React.FC = () => {
  const { clientId, workflowId } = useParams<{ clientId: string; workflowId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showToast } = useToast();
  
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResuming, setIsResuming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isReprocessingErrors, setIsReprocessingErrors] = useState(false);
  const [finalizedPrompt, setFinalizedPrompt] = useState<string | null>(null);
  const [currentModel, setCurrentModel] = useState<string>('openai-gpt-5-nano');
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [batchMode, setBatchMode] = useState<string>('snowflake');
  const [businessConfig, setBusinessConfig] = useState<any>(null);
  
  // Check if most recent module is from upstream steps
  const { 
    mostRecentModuleIsUpstream, 
    isCheckingModules, 
    setMostRecentModuleIsUpstream 
  } = useUpstreamModuleCheck(workflowId, ['fileupload_v2', 'module1_v2', 'module3_v2', 'module4-config_v2']);
  
  
  // Results dialog state
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [resultsDialogInitialFilter, setResultsDialogInitialFilter] = useState<'all' | 'completed' | 'failed'>('all');
  
  // Edit prompt and re-run dialog state
  const [editPromptDialogOpen, setEditPromptDialogOpen] = useState(false);
  const [isStartingNewBatch, setIsStartingNewBatch] = useState(false);
  
  // Track first observation for client-side speed calculation
  const [firstObservation, setFirstObservation] = useState<{
    timestamp: number;
    processedKeywords: number;
  } | null>(null);
  
  // Track polling start time for dynamic interval
  const [pollingStartTime, setPollingStartTime] = useState<number | null>(null);
  
  // Fetch batch status function (extracted for reuse)
  const fetchStatus = useCallback(async () => {
    if (!workflowId) return;
    
    try {
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-status/${workflowId}`;
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        setBatchStatus(data);
        
        // Track first observation when we start seeing processed keywords
        if (data.processed_keywords > 0 && data.status === 'processing') {
          if (!firstObservation) {
            setFirstObservation({
              timestamp: Date.now(),
              processedKeywords: data.processed_keywords
            });
          }
          // If processed count went backwards (batch restarted), reset observation
          else if (data.processed_keywords < firstObservation.processedKeywords) {
            setFirstObservation({
              timestamp: Date.now(),
              processedKeywords: data.processed_keywords
            });
          }
        }
        
        // Reset first observation if batch is completed or failed
        if (data.status === 'completed' || data.status === 'failed') {
          setFirstObservation(null);
        }
      }
    } catch (error) {
      console.error('Error fetching batch status:', error);
    } finally {
      setIsLoading(false);
    }
  }, [workflowId, firstObservation]);
  
  const handleResumeBatch = async (reprocessErrorsOnly: boolean = false) => {
    if (!batchStatus?.external_job_id) return;
    
    const isErrorReprocess = reprocessErrorsOnly;
    if (isErrorReprocess) {
      setIsReprocessingErrors(true);
    } else {
      setIsResuming(true);
    }
    
    try {
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-resume/${batchStatus.external_job_id}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reprocess_errors_only: reprocessErrorsOnly
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        const successMsg = isErrorReprocess 
          ? `Reprocessing ${batchStatus.error_records} failed queries`
          : 'Batch processing resumed successfully';
        showToast(successMsg, 'success');
        
        // Reset first observation to get fresh speed calculations
        setFirstObservation(null);
        
        // Immediately fetch status to update UI (no delay)
        await fetchStatus();
        
        // Keep loading state true for a moment to ensure smooth transition
        setTimeout(() => {
          if (isErrorReprocess) {
            setIsReprocessingErrors(false);
          } else {
            setIsResuming(false);
          }
        }, 1000);
      } else {
        const error = await response.json();
        const errorMsg = error.detail || `Failed to ${isErrorReprocess ? 'reprocess errors' : 'resume batch'}`;
        
        // Show appropriate message
        if (errorMsg.includes('currently running') || errorMsg.includes('actively processing')) {
          showToast('Batch is still running. Please wait a moment before retrying.', 'warning');
        } else {
          showToast(errorMsg, 'error');
        }
        
        if (isErrorReprocess) {
          setIsReprocessingErrors(false);
        } else {
          setIsResuming(false);
        }
      }
    } catch (error) {
      console.error(`Error ${isErrorReprocess ? 'reprocessing errors' : 'resuming batch'}:`, error);
      showToast(`Failed to ${isErrorReprocess ? 'reprocess error queries' : 'resume batch processing'}`, 'error');
      
      if (isErrorReprocess) {
        setIsReprocessingErrors(false);
      } else {
        setIsResuming(false);
      }
    }
  };
  
  const handleCancelBatch = async () => {
    if (!batchStatus?.external_job_id) return;
    
    setShowCancelDialog(true);
  };

  const confirmCancelBatch = async () => {
    setShowCancelDialog(false);
    if (!batchStatus?.external_job_id) return;
    
    setIsCancelling(true);
    try {
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-job/cancel/${batchStatus.external_job_id}`;
      const response = await fetch(apiUrl, {
        method: 'POST'
      });

      if (response.ok) {
        showToast('Batch processing cancelled successfully', 'info');
        
        // Fetch final status
        await fetchStatus();
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to cancel batch', 'error');
      }
    } catch (error) {
      console.error('Error cancelling batch:', error);
      showToast('Failed to cancel batch processing', 'error');
    } finally {
      setIsCancelling(false);
    }
  };
  
  const handleOpenEditPromptDialog = () => {
    setEditPromptDialogOpen(true);
  };
  
  const handleCloseEditPromptDialog = () => {
    setEditPromptDialogOpen(false);
  };
  
  const handleStartNewBatchWithPrompt = async (editedPrompt: string, model: string) => {
    if (!clientId || !workflowId || !editedPrompt.trim()) {
      showToast('Please enter a valid prompt', 'error');
      return;
    }
    
    setIsStartingNewBatch(true);
    try {
      // First, fetch the existing config from module4-config_v2
      const configUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-config/${workflowId}`;
      const configResponse = await fetch(configUrl);
      
      if (!configResponse.ok) {
        throw new Error('Failed to fetch existing configuration');
      }
      
      const configData = await configResponse.json();
      if (!configData.config) {
        throw new Error('No configuration found');
      }
      
      // Parse config if it's a string
      const existingConfig = typeof configData.config === 'string' 
        ? JSON.parse(configData.config) 
        : configData.config;
      
      // Extract and store batch_mode from config
      if (existingConfig.batch_mode) {
        setBatchMode(existingConfig.batch_mode);
      }
      
      // Call finalize endpoint with config and current batch_id
      // Backend will extract batch_mode from current batch if batch_id is provided
      const finalizeUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-config/finalize`;
      const finalizeResponse = await fetch(finalizeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId,
          config: existingConfig,
          finalized_prompt: editedPrompt,
          current_batch_id: batchStatus?.external_job_id,  // Pass current batch_id, backend will get batch_mode
          model_name: model
        }),
      });
      
      if (finalizeResponse.ok) {
        const finalizeData = await finalizeResponse.json();
        showToast('New batch started successfully with modified prompt', 'success');
        setEditPromptDialogOpen(false);
        
        // New batch module created, so upstream is no longer most recent
        setMostRecentModuleIsUpstream(false);
        
        // Reset observations
        setFirstObservation(null);
        setPollingStartTime(null);
        
        // IMPORTANT: Set the new batch status immediately from the finalize response
        // This triggers the polling useEffect which will immediately call poll() and start the interval
        setBatchStatus({
          module_id: finalizeData.batch_module_id,
          job_id: finalizeData.batch_job_id,
          external_job_id: finalizeData.batch_id,
          status: 'processing',
          is_active_running: true,  // This triggers polling to start immediately
          total_keywords: finalizeData.total_keywords,
          processed_keywords: 0,
          error_records: 0,
          progress_percentage: 0,
          started_at: new Date().toISOString(),
          completed_at: null,
          error_message: null
        });
      } else {
        const error = await finalizeResponse.json();
        showToast(error.detail || 'Failed to start new batch', 'error');
      }
    } catch (error) {
      console.error('Error starting new batch:', error);
      showToast('Failed to start new batch', 'error');
    } finally {
      setIsStartingNewBatch(false);
    }
  };
  
  // Fetch finalized prompt, model, and batch mode
  useEffect(() => {
    if (!workflowId) return;
    
    const fetchPromptAndConfig = async () => {
      try {
        let foundBatchMode = false;
        
        // Fetch prompt, model, batch_mode, and business_config from metadata (most recent batch)
        const metadataUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-metadata/${workflowId}`;
        const metadataResponse = await fetch(metadataUrl);
        
        if (metadataResponse.ok) {
          const data = await metadataResponse.json();
          
          if (data.finalized_prompt) {
            setFinalizedPrompt(data.finalized_prompt);
          }
          if (data.model_name) {
            setCurrentModel(data.model_name);
          }
          // Get batch_mode from the actual batch metadata (most recent batch)
          if (data.batch_mode) {
            setBatchMode(data.batch_mode);
            foundBatchMode = true;
          }
          // Get business_config from metadata
          if (data.business_config) {
            setBusinessConfig(data.business_config);
          }
        }
        
        // Fallback: If batch_mode not in metadata, fetch from config
        if (!foundBatchMode) {
          const configUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-config/${workflowId}`;
          const configResponse = await fetch(configUrl);
          
          if (configResponse.ok) {
            const configData = await configResponse.json();
            
            if (configData.config) {
              const existingConfig = typeof configData.config === 'string' 
                ? JSON.parse(configData.config) 
                : configData.config;
              
              if (existingConfig.batch_mode) {
                setBatchMode(existingConfig.batch_mode);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching prompt and config:', error);
      }
    };
    
    fetchPromptAndConfig();
  }, [workflowId]);
  
  // Poll for status updates with dynamic interval
  useEffect(() => {
    if (!workflowId) return;
    
    // Initial fetch
    fetchStatus();
    
    // Track if we're actively polling
    const isActivelyPolling = batchStatus?.status === 'processing' && batchStatus?.is_active_running;
    
    // Initialize polling start time when we start actively polling
    if (isActivelyPolling && !pollingStartTime) {
      setPollingStartTime(Date.now());
    }
    
    // Reset polling start time when polling stops
    if (!isActivelyPolling && pollingStartTime) {
      setPollingStartTime(null);
    }
    
    const getPollingInterval = () => {
      return 5000; // 5 seconds - consistent polling interval
    };
    
    // Initial interval
    let currentInterval = getPollingInterval();
    
    const poll = () => {
      if (batchStatus?.status === 'processing' && batchStatus?.is_active_running) {
        fetchStatus();
      }
    };
    
    // Call poll immediately if we're actively polling (don't wait for first interval)
    if (isActivelyPolling) {
      poll();
    }
    
    // Set up interval for subsequent polls
    let intervalId = setInterval(poll, currentInterval);
    
    // Check if we need to switch interval after 1 minute
    let switchTimeout: NodeJS.Timeout | null = null;
    if (pollingStartTime && currentInterval === 3000) {
      const timeUntilSwitch = 60000 - (Date.now() - pollingStartTime);
      if (timeUntilSwitch > 0) {
        switchTimeout = setTimeout(() => {
          clearInterval(intervalId);
          intervalId = setInterval(poll, 5000);
        }, timeUntilSwitch);
      }
    }
    
    return () => {
      clearInterval(intervalId);
      if (switchTimeout) clearTimeout(switchTimeout);
    };
  }, [workflowId, batchStatus?.status, batchStatus?.is_active_running, pollingStartTime, fetchStatus]);
  
  const getStatusIcon = () => {
    const icon = (() => {
      switch (batchStatus?.status) {
        case 'processing':
          return <ProcessingIcon sx={{ fontSize: 48, color: 'primary.main' }} />;
        case 'completed':
          return <CompletedIcon sx={{ fontSize: 48, color: 'success.main' }} />;
        case 'failed':
          return <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />;
        case 'cancelled':
          return <ErrorIcon sx={{ fontSize: 48, color: 'grey.500' }} />;
        case 'pending':
          return <ProcessingIcon sx={{ fontSize: 48, color: 'warning.main' }} />;
        default:
          return <ProcessingIcon sx={{ fontSize: 48, color: 'text.secondary' }} />;
      }
    })();
    
    // Add spinning circular progress when actively running
    if (batchStatus?.status === 'processing' && batchStatus?.is_active_running) {
      return (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            size={64}
            thickness={2}
            sx={{ color: 'primary.main', position: 'absolute', top: -8, left: -8 }}
          />
          {icon}
        </Box>
      );
    }
    
    return icon;
  };
  
  const getStatusColor = () => {
    switch (batchStatus?.status) {
      case 'processing':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };
  
  const progress = batchStatus?.progress_percentage !== undefined 
    ? batchStatus.progress_percentage
    : (batchStatus?.total_keywords && batchStatus?.processed_keywords
        ? (batchStatus.processed_keywords / batchStatus.total_keywords) * 100
        : 0);
  
  // Calculate timing metrics based on client-side observations
  const getTimingMetrics = () => {
    // For completed batches, use server timestamps if available
    if (batchStatus?.status === 'completed' && batchStatus?.started_at && batchStatus?.completed_at) {
      const startTime = new Date(batchStatus.started_at).getTime();
      const endTime = new Date(batchStatus.completed_at).getTime();
      const elapsedMs = endTime - startTime;
      const elapsedSeconds = elapsedMs / 1000;
      
      const formatElapsedTime = () => {
        const hours = Math.floor(elapsedSeconds / 3600);
        const minutes = Math.floor((elapsedSeconds % 3600) / 60);
        const seconds = Math.floor(elapsedSeconds % 60);
        
        if (hours > 0) {
          return `${hours}h ${minutes}m ${seconds}s`;
        } else if (minutes > 0) {
          return `${minutes}m ${seconds}s`;
        } else {
          return `${seconds}s`;
        }
      };
      
      const speed = elapsedSeconds > 0 && batchStatus.processed_keywords 
        ? batchStatus.processed_keywords / elapsedSeconds 
        : 0;
      
      return {
        totalTime: formatElapsedTime(),
        speed: speed,
        speedFormatted: `${speed.toFixed(2)} queries/sec`,
        eta: null,
        startedAt: batchStatus.started_at
      };
    }
    
    // For active processing, use client-side observations
    if (!firstObservation || !batchStatus?.processed_keywords) {
      return null;
    }
    
    const currentTime = Date.now();
    const elapsedMs = currentTime - firstObservation.timestamp;
    const elapsedSeconds = elapsedMs / 1000;
    
    // Calculate keywords processed since first observation
    const keywordsProcessed = batchStatus.processed_keywords - firstObservation.processedKeywords;
    
    // Calculate speed based on observed progress
    const speed = elapsedSeconds > 0 ? keywordsProcessed / elapsedSeconds : 0;
    
    // Calculate ETA for remaining keywords
    let eta = null;
    if (speed > 0 && batchStatus.total_keywords && batchStatus.status === 'processing') {
      const remainingKeywords = batchStatus.total_keywords - batchStatus.processed_keywords;
      const remainingSeconds = remainingKeywords / speed;
      const remainingMinutes = remainingSeconds / 60;
      const remainingHours = remainingMinutes / 60;
      
      if (remainingHours >= 1) {
        const hours = Math.floor(remainingHours);
        const minutes = Math.floor(remainingMinutes % 60);
        eta = `${hours}h ${minutes}m`;
      } else if (remainingMinutes >= 1) {
        eta = `${Math.floor(remainingMinutes)}m`;
      } else if (remainingSeconds >= 1) {
        eta = `${Math.floor(remainingSeconds)}s`;
      } else {
        eta = `< 1s`;
      }
    }
    
    return {
      speed: speed,
      speedFormatted: `${speed.toFixed(2)} queries/sec`,
      eta: eta,
      startedAt: batchStatus?.started_at
    };
  };
  
  const timingMetrics = getTimingMetrics();
  
  // Format started time for display in user's local timezone
  const formatStartedTime = (timestamp: string | undefined) => {
    if (!timestamp) return null;
    // Ensure UTC timestamp is properly parsed and converted to local time
    const date = new Date(timestamp.endsWith('Z') ? timestamp : timestamp + 'Z');
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  // Show loading spinner during initial module check or batch status loading
  if (isCheckingModules || isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading batch status...
          </Typography>
        </Box>
      </Box>
    );
  }
  
  // Show warning if upstream module is most recent
  if (mostRecentModuleIsUpstream) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Batch Not Ready
          </Typography>
          <Typography variant="body2">
            The workflow has been re-run from an earlier step. Please complete all previous steps (File Upload, Keyword Analysis, Semantic Analysis, and Batch Configuration) before running the batch.
          </Typography>
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          backgroundColor: alpha(theme.palette.background.paper, 0.9)
        }}
      >
          {/* Status Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          {getStatusIcon()}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Batch Processing
              </Typography>
              <Chip 
                label={batchStatus?.status || 'Unknown'}
                color={getStatusColor() as any}
                size="small"
              />
              {currentModel && batchMode === 'snowflake' && (
                <Chip 
                  label={currentModel === 'openai-gpt-5-nano' ? 'GPT-5 Nano' : 'GPT-5 Mini'}
                  variant="outlined"
                  size="small"
                  color="primary"
                />
              )}
              {batchStatus?.external_job_id && (
                <Tooltip title="Review batch results" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      setResultsDialogInitialFilter('all');
                      setResultsDialogOpen(true);
                    }}
                    sx={{ 
                      padding: 0.5,
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                    }}
                  >
                    <ViewResultsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {batchStatus?.external_job_id && (
              <CopyableText 
                text={batchStatus.external_job_id} 
                label="Batch ID"
                variant="body2"
              />
            )}
          </Box>
        </Box>
        
        {/* Progress */}
        {batchStatus?.status === 'processing' && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {batchStatus?.processed_keywords || 0} / {batchStatus?.total_keywords || 0} queries
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {progress.toFixed(1)}% complete
              </Typography>
              
              {/* Timing metrics */}
              {(timingMetrics || batchStatus?.started_at) && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {batchStatus?.started_at && (
                    <Chip 
                      label={`🕐 Started: ${formatStartedTime(batchStatus.started_at)}`} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                  {timingMetrics?.speedFormatted && (
                    <Chip 
                      label={`⚡ ${timingMetrics.speedFormatted}`} 
                      size="small" 
                      variant="outlined"
                      color="primary"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                  {timingMetrics?.eta && (
                    <Chip 
                      label={`🎯 ETA: ${timingMetrics.eta}`} 
                      size="small" 
                      variant="outlined"
                      color="success"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
        
        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          {batchStatus?.total_keywords !== undefined && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total queries
              </Typography>
              <Typography variant="h4" color="primary">
                {batchStatus.total_keywords.toLocaleString()}
              </Typography>
            </Box>
          )}
          {batchStatus?.processed_keywords !== undefined && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Processed
              </Typography>
              <Typography variant="h4" color="success.main">
                {batchStatus.processed_keywords.toLocaleString()}
              </Typography>
            </Box>
          )}
          {batchStatus?.error_records !== undefined && batchStatus.error_records > 0 && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Errors
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4" color="error.main">
                    {batchStatus.error_records.toLocaleString()}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setResultsDialogInitialFilter('failed');
                      setResultsDialogOpen(true);
                    }}
                    sx={{ 
                      padding: 0.5,
                      '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                    }}
                    title="View error details"
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              
            </Box>
          )}
          
          {/* Show timing metrics for completed batches */}
          {batchStatus?.status === 'completed' && timingMetrics && (
            <>
              {timingMetrics.startedAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="h6" color="text.primary">
                    {formatStartedTime(timingMetrics.startedAt)}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Time
                </Typography>
                <Typography variant="h6" color="text.primary">
                  {timingMetrics.totalTime}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Speed
                </Typography>
                <Typography variant="h6" color="text.primary">
                  {timingMetrics.speedFormatted}
                </Typography>
              </Box>
            </>
          )}
        </Box>
        
        {/* Error Message & Resume/Cancel Buttons */}
        {(batchStatus?.status === 'failed' || batchStatus?.status === 'pending' || batchStatus?.status === 'processing' || batchStatus?.status === 'cancelled') && (
          <>
            {batchStatus?.error_message && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Error:
                </Typography>
                <Typography variant="body2">
                  {batchStatus.error_message}
                </Typography>
              </Alert>
            )}
            
            {/* Show info if batch is actively running */}
            {batchStatus?.is_active_running && (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Batch is actively processing
                </Typography>
                <Typography variant="body2">
                  The batch is currently running in the background. Progress updates will appear automatically.
                </Typography>
              </Alert>
            )}
            
            {/* Show warning if status is processing but NOT actively running */}
            {batchStatus?.status === 'processing' && !batchStatus?.is_active_running && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Batch appears to be stuck
                </Typography>
                <Typography variant="body2">
                  The batch is marked as processing but may have been interrupted by a server restart or crash. 
                  You can force-resume to restart processing from where it stopped.
                </Typography>
              </Alert>
            )}
            
            {/* Action Buttons - Resume and Cancel */}
            {batchStatus?.status === 'processing' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                {/* Resume Button - Show when not actively running */}
                {!batchStatus?.is_active_running && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={
                      isResuming 
                        ? <CircularProgress size={20} color="inherit" /> 
                        : <ReplayIcon />
                    }
                    onClick={() => handleResumeBatch(false)}
                    disabled={isResuming || isReprocessingErrors || isCancelling}
                    sx={{ minWidth: 200 }}
                  >
                    {isResuming ? 'Starting...' : 'Resume Batch Process'}
                  </Button>
                )}
                
                {/* Cancel Button - Show when actively running */}
                {batchStatus?.is_active_running && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    startIcon={
                      isCancelling 
                        ? <CircularProgress size={20} color="inherit" /> 
                        : <DeleteIcon />
                    }
                    onClick={handleCancelBatch}
                    disabled={isCancelling || isResuming}
                    sx={{ minWidth: 200 }}
                  >
                    {isCancelling ? 'Cancelling...' : 'Cancel Batch Process'}
                  </Button>
                )}
              </Box>
            )}

            {/* Resume Button - Show when not actively running and not processing */}
            {!batchStatus?.is_active_running && batchStatus?.status !== 'processing' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={
                    isResuming 
                      ? <CircularProgress size={20} color="inherit" /> 
                      : batchStatus?.status === 'pending' 
                        ? <ProcessingIcon /> 
                        : <ReplayIcon />
                  }
                  onClick={() => handleResumeBatch(false)}
                  disabled={isResuming || isReprocessingErrors || isCancelling}
                  sx={{ minWidth: 200 }}
                >
                  {isResuming 
                    ? 'Starting...' 
                    : 'Resume Batch Process'}
                </Button>
              </Box>
            )}
          </>
        )}
        
        {/* Cancellation Message */}
        {batchStatus?.status === 'cancelled' && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2">
              Batch processing was cancelled. {batchStatus.processed_keywords || 0} out of {batchStatus.total_keywords || 0} queries were completed before cancellation.
            </Typography>
          </Alert>
        )}
        
        {/* Info - Only show when not completed and not cancelled */}
        {batchStatus?.status !== 'completed' && batchStatus?.status !== 'cancelled' && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              This process may take several minutes to hours depending on the number of queries.
              You can safely close this page - the processing will continue in the background.
              {batchStatus?.status === 'failed' && ' If processing fails, you can resume from where it stopped using the Resume button above.'}
            </Typography>
          </Alert>
        )}
      </Paper>
      
      {/* Re-run Batch with Modified Prompt - Only show when completed, not when cancelled */}
      {batchStatus?.status === 'completed' && (
        <Alert 
          severity="success"
          sx={{ 
            mt: 3, 
            mb: 2,
            backgroundColor: alpha(theme.palette.success.main, 0.05),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Batch Complete - Ready for Next Run
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {((batchStatus?.error_records ?? 0) > 0)
              ? 'Choose your next step: Reprocess failed queries to retry errors, Modify & Re-run to iterate on your results with a refined prompt using the same queries, or Proceed to SERP Analysis to continue the workflow.'
              : 'Choose your next step: Proceed to SERP Analysis to continue the workflow, or Modify & Re-run to iterate on your results with a refined prompt using the same queries.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Reprocess Errors Button - Only show if there are errors */}
            {(batchStatus?.error_records ?? 0) > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="medium"
                startIcon={
                  isReprocessingErrors 
                    ? <CircularProgress size={20} color="inherit" /> 
                    : <ReplayIcon />
                }
                onClick={() => handleResumeBatch(true)}
                disabled={isResuming || isReprocessingErrors}
                sx={{ 
                  minWidth: 180,
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}
              >
                {isReprocessingErrors ? 'Starting...' : 'Reprocess Errors'}
              </Button>
            )}
            <Button
              variant="outlined"
              color={batchStatus?.status === 'completed' ? 'success' : 'primary'}
              size="medium"
              startIcon={<ReplayIcon />}
              onClick={handleOpenEditPromptDialog}
              sx={{ 
                minWidth: 180,
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              Modify & Re-run
            </Button>
            <Button
              variant="contained"
              color="success"
              size="medium"
              onClick={() => navigate(`/workflow2/${clientId}/${workflowId}/serp_tool`)}
              sx={{ 
                minWidth: 180,
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              Proceed to SERP Analysis →
            </Button>
          </Box>
        </Alert>
      )}
      
      {/* Finalized Prompt */}
      {finalizedPrompt && (
        <Box sx={{ mt: 3 }}>
          <Accordion 
            expanded={promptExpanded} 
            onChange={() => setPromptExpanded(!promptExpanded)}
            sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.03) }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Finalized Prompt
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This is the finalized prompt being used to process all queries in this batch.
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: '600px', overflow: 'auto' }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      margin: 0
                    }}
                  >
                    {finalizedPrompt}
                  </Typography>
                  
                  {/* Business Context Section */}
                  {businessConfig && Object.keys(businessConfig).length > 0 && (
                    <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          mb: 2,
                          color: 'primary.main'
                        }}
                      >
                        BUSINESS CONTEXT:
                      </Typography>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          margin: 0
                        }}
                      >
                        {JSON.stringify(businessConfig, null, 2)}
                      </Typography>
                    </Box>
                  )}
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Prompt length: {finalizedPrompt.length} characters
                  {businessConfig && Object.keys(businessConfig).length > 0 && 
                    ` | Business context: ${JSON.stringify(businessConfig).length} characters`
                  }
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      
      {/* Batch Results Dialog */}
      {batchStatus?.external_job_id && (
        <BatchResultsDialog
          open={resultsDialogOpen}
          onClose={() => setResultsDialogOpen(false)}
          batchId={batchStatus.external_job_id}
          initialFilter={resultsDialogInitialFilter}
        />
      )}
      
      {/* Edit Prompt and Re-run Dialog */}
      <PromptEditDialog
        open={editPromptDialogOpen}
        onClose={handleCloseEditPromptDialog}
        onConfirm={handleStartNewBatchWithPrompt}
        initialPrompt={finalizedPrompt || ''}
        initialModel={currentModel}
        totalQueries={batchStatus?.total_keywords}
        title="Modify Prompt & Start New Batch"
        description="Modify the prompt below to refine your AI analysis. The new batch will process all queries from this workflow with your updated prompt. Previous results will remain accessible for comparison."
        confirmButtonText="Start New Batch"
        isProcessing={isStartingNewBatch}
        batchMode={batchMode}
      />
      
      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        title="Cancel Batch Processing"
        message="Are you sure you want to cancel this batch processing? Currently processing queries will complete, but no new queries will be started. This action cannot be undone."
        confirmText="Yes, Cancel Batch"
        cancelText="No, Keep Running"
        confirmColor="error"
        onConfirm={confirmCancelBatch}
        onCancel={() => setShowCancelDialog(false)}
      />
    </Box>
  );
};

export default BatchRunStep;
